import { z } from 'zod';
import { EvidenceId, Lang, QuestionId, type L10n, type Track } from './common.js';

/**
 * Aera services the brief may map an opportunity to. The consulting ids mirror the services and pricing
 * sections of the landing page; the overseas ids are the four products on the 出海 page plus its expert call.
 * Each track's catalogue (in @aera/content) lists the ids its briefs may use.
 */
export const ServiceId = z.enum([
  'free_diagnostic',
  'aera_workspace',
  'aera_forge',
  'aera_agent',
  'custom_automation',
  'system_assembly',
  'integration',
  'agent_factory',
  'knowledge_base',
  'managed_ops',
  'training',
  'strategy_diagnosis',
  'brand_website',
  'outbound_engine',
  'nextgen_marketing',
  'expert_call',
]);
export type ServiceId = z.infer<typeof ServiceId>;

/** The E1 (consulting) and OF3 (overseas) option tags; capabilities map to services. */
export const CapabilityTag = z.enum([
  'document_capture',
  'reporting',
  'knowledge_qa',
  'forecasting',
  'workflow',
  'content',
  'vision',
  'integration',
  'agent',
  'tool_builder',
  'market_analysis',
  'compliance_map',
  'prospecting',
  'outreach',
  'website',
  'localisation',
  'social',
  'lead_capture',
]);
export type CapabilityTag = z.infer<typeof CapabilityTag>;

const Score = z.number().int().min(1).max(5);
const Confidence = z.enum(['high', 'medium', 'low']);
const Short = z.string().max(600);

/**
 * The internal brief: the only call that reasons. The model fills this schema; the client brief is
 * rewritten from it, so the client brief can never leak a qualification score or invent anything.
 */
export const InternalBrief = z.object({
  language: Lang,
  profile: z.object({
    industry: z.string().max(80),
    sizeBand: z.string().max(80),
    respondentRole: z.string().max(80),
    decisionPower: z.enum(['owner', 'needs_approval', 'unknown']),
  }),
  /** ≤ 120 words: what work looks like today. */
  situation: z.string().max(1500),
  /** Ranked by severity × frequency × consequence, max 5. */
  pains: z
    .array(
      z.object({
        title: z.string().max(160),
        evidence: z.array(EvidenceId).min(1),
        peopleHoursPerWeek: z.number().min(0).max(10000).nullable(),
        consequence: Short,
        whyUnsolved: z.array(z.string().max(300)).max(6),
        severity: Score,
        confidence: Confidence,
      }),
    )
    .max(5),
  outcomes: z
    .array(z.object({ outcome: z.string().max(300), successMetric: z.string().max(300), horizon: z.string().max(80) }))
    .max(5),
  opportunities: z
    .array(
      z.object({
        title: z.string().max(160),
        capability: CapabilityTag,
        service: ServiceId,
        whatItDoes: Short,
        inputsNeeded: z.array(z.string().max(200)).max(8),
        dataReadiness: Score,
        feasibility: Score,
        value: Score,
        first30Days: Short,
      }),
    )
    .max(4),
  firstStep: z.object({
    title: z.string().max(160),
    whyFirst: Short,
    scope: Short,
    expectedResult: Short,
    prerequisites: z.array(z.string().max(300)).max(8),
  }),
  constraints: z.object({
    budgetBand: z.string().max(200),
    timeline: z.string().max(200),
    dataResidency: z.string().max(300),
    keepSystems: z.array(z.string().max(200)).max(8),
    teamCapacity: z.string().max(300),
  }),
  people: z.object({
    decisionMaker: z.string().max(200),
    dailyUsers: z.array(z.string().max(120)).max(10),
    internalOwner: z.string().max(200),
    changeReadiness: Score.nullable(),
  }),
  qualification: z.object({
    fit: z.enum(['strong', 'possible', 'weak']),
    budgetSignal: z.string().max(300),
    authoritySignal: z.string().max(300),
    urgencySignal: z.string().max(300),
    riskFlags: z.array(z.string().max(300)).max(8),
  }),
  contradictions: z.array(z.object({ between: z.array(QuestionId).min(2), note: z.string().max(400) })).max(6),
  gaps: z.array(z.string().max(300)).max(10),
  /** 5 to 8 questions worth asking on the free diagnostic call. */
  questionsForTheCall: z.array(z.string().max(300)).min(5).max(8),
  overallConfidence: Confidence,
  ambiguityNote: z.string().max(600),
});
export type InternalBrief = z.infer<typeof InternalBrief>;

/** The overseas flywheel: Insight → Build → Reach → Amplify → Learn. */
export const FlywheelStage = z.enum(['insight', 'build', 'reach', 'amplify', 'learn']);
export type FlywheelStage = z.infer<typeof FlywheelStage>;

/** The overseas track's internal brief: everything above plus the markets, ICP, outreach and compliance view. */
export const InternalBriefOverseas = InternalBrief.extend({
  /** Ranked with reasons and a readiness score. */
  markets: z
    .array(
      z.object({
        market: z.string().max(80),
        reasons: z.array(z.string().max(300)).min(1).max(5),
        readiness: Score,
        evidence: z.array(EvidenceId).min(1),
      }),
    )
    .max(5),
  /** The one-sentence ideal-customer brief Aera Sales takes as input. */
  icpDraft: z.string().max(400),
  outreachReadiness: z.object({
    domain: z.string().max(200),
    mailbox: z.string().max(200),
    listSource: z.string().max(200),
    /** The outreach rule for each chosen market, from the rules table; counsel confirms before the pilot. */
    rules: z.array(z.object({ market: z.string().max(80), rule: z.string().max(500) })).max(8),
  }),
  complianceFlags: z
    .array(
      z.object({
        kind: z.enum(['certification', 'trademark', 'export_control', 'data_privacy', 'sector', 'other']),
        market: z.string().max(80).nullable(),
        note: z.string().max(400),
      }),
    )
    .max(10),
  assetGaps: z.array(z.string().max(300)).max(10),
  /** One starting product, then the next ones in flywheel order. */
  recommendedSequence: z
    .array(z.object({ stage: FlywheelStage, service: ServiceId, why: z.string().max(400) }))
    .min(1)
    .max(5),
});
export type InternalBriefOverseas = z.infer<typeof InternalBriefOverseas>;

export const internalBriefSchemaFor = (track: Track) => (track === 'overseas' ? InternalBriefOverseas : InternalBrief);

export const INTERNAL_BRIEF_SCHEMA_VERSION = 1;

/** Follow-up call output: at most three short questions that close real gaps. */
export const FollowupsOutput = z.object({
  followups: z
    .array(
      z.object({
        prompt: z.string().min(4).max(200),
        type: z.enum(['single', 'multi', 'text']),
        options: z.array(z.string().min(1).max(80)).max(8),
        allowOther: z.boolean(),
        reason: z.string().min(3).max(200),
      }),
    )
    .max(3),
});
export type FollowupsOutput = z.infer<typeof FollowupsOutput>;

/**
 * The client brief is Markdown prose so it can stream as writing: six fixed headings in a fixed order,
 * validated after generation. The first section is the lede and the page shows it without its heading.
 */
export const CLIENT_BRIEF_HEADINGS_BY_TRACK: Record<Track, Record<Lang, readonly string[]>> = {
  consulting: {
    zh: ['概述', '我们了解到的情况', '当前最突出的问题', '期望达成的效果', '建议的切入点', '后续安排'],
    en: ['Opening', 'What we heard', 'The one that hurts most', 'What done looks like', 'Where we suggest starting', 'What happens next'],
  },
  overseas: {
    zh: ['概述', '我们了解到的情况', '建议优先进入的市场', '当前最突出的问题', '期望达成的效果', '建议的切入点', '后续安排'],
    en: ['Opening', 'What we heard', 'The market to start with', 'The one that hurts most', 'What done looks like', 'Where we suggest starting', 'What happens next'],
  },
};

/** The consulting track's headings. */
export const CLIENT_BRIEF_HEADINGS: Record<Lang, readonly string[]> = CLIENT_BRIEF_HEADINGS_BY_TRACK.consulting;

export interface ClientBriefSection {
  heading: string;
  body: string;
}

/** Splits a client brief on its `## ` headings. Text before the first heading is ignored. */
export function splitClientBrief(markdown: string): ClientBriefSection[] {
  const sections: ClientBriefSection[] = [];
  let current: ClientBriefSection | null = null;
  for (const line of markdown.replace(/\r\n/g, '\n').split('\n')) {
    const m = /^##\s+(.+?)\s*#*\s*$/.exec(line);
    if (m) {
      if (current) sections.push(current);
      current = { heading: m[1]!.trim(), body: '' };
    } else if (current) {
      current.body += `${line}\n`;
    }
  }
  if (current) sections.push(current);
  return sections.map((s) => ({ heading: s.heading, body: s.body.trim() }));
}

/** Arabic numerals in a text, normalised ("1,200" -> "1200", "2.50" -> "2.5"). Chinese numerals are not digits. */
export function extractNumbers(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(/\d[\d,]*(?:\.\d+)?/g)) {
    const raw = m[0].replace(/,(?=\d{3}\b)/g, '').replace(/,$/, '');
    const n = Number(raw);
    if (Number.isFinite(n)) out.push(String(n));
  }
  return out;
}

export interface ClientBriefIssue {
  code: 'missing_heading' | 'unexpected_heading' | 'heading_order' | 'unknown_number' | 'too_long' | 'too_short' | 'wrong_language' | 'forbidden_term' | 'informal_address';
  detail: string;
}

/** Terms that must never reach the client: scores, service ids, internal product names, prices. */
const FORBIDDEN = [
  /\bfit\b\s*[:：]/i,
  /qualification/i,
  /\b(free_diagnostic|aera_workspace|aera_forge|aera_agent|custom_automation|system_assembly|agent_factory|knowledge_base|managed_ops|strategy_diagnosis|brand_website|outbound_engine|nextgen_marketing|expert_call)\b/,
  /Aera\s+(Forge|Sales)/i,
  /[¥￥$]\s?\d/,
  /\d\s?(元|万元|块钱)/,
];

export function validateClientBrief(
  markdown: string,
  lang: Lang,
  allowedNumbers: Iterable<string>,
  track: Track = 'consulting',
): ClientBriefIssue[] {
  const issues: ClientBriefIssue[] = [];
  const expected = CLIENT_BRIEF_HEADINGS_BY_TRACK[track][lang];
  const sections = splitClientBrief(markdown);
  const headings = sections.map((s) => s.heading);
  for (const h of expected) {
    if (!headings.includes(h)) issues.push({ code: 'missing_heading', detail: h });
  }
  for (const h of headings) {
    if (!expected.includes(h)) issues.push({ code: 'unexpected_heading', detail: h });
  }
  const order = expected.filter((h) => headings.includes(h));
  const seen = headings.filter((h) => expected.includes(h));
  if (order.join('|') !== seen.join('|')) issues.push({ code: 'heading_order', detail: seen.join(' / ') });

  const allowed = new Set(allowedNumbers);
  const unknown = [...new Set(extractNumbers(markdown))].filter((n) => !allowed.has(n));
  for (const n of unknown) issues.push({ code: 'unknown_number', detail: n });

  const length = lang === 'zh' ? markdown.replace(/\s/g, '').length : markdown.split(/\s+/).length;
  const [min, max] = lang === 'zh' ? [200, 2600] : [120, 1400];
  if (length > max) issues.push({ code: 'too_long', detail: String(length) });
  if (length < min) issues.push({ code: 'too_short', detail: String(length) });

  const han = (markdown.match(/[一-鿿]/g) ?? []).length;
  const latin = (markdown.match(/[A-Za-z]/g) ?? []).length;
  if (lang === 'zh' && han < latin) issues.push({ code: 'wrong_language', detail: `han ${han} < latin ${latin}` });
  if (lang === 'en' && han > latin / 4) issues.push({ code: 'wrong_language', detail: `han ${han} in an English brief` });

  for (const re of FORBIDDEN) {
    const m = re.exec(markdown);
    if (m) issues.push({ code: 'forbidden_term', detail: m[0] });
  }

  // The client is addressed formally (您 / 贵司); 你 outside a quotation of their own words is not.
  if (lang === 'zh') {
    const m = informalAddress(markdown);
    if (m) issues.push({ code: 'informal_address', detail: m });
  }
  return issues;
}

/**
 * The first informal address (你 / 妳 / 你们) in Chinese text, or null. Short single-line quotations of the
 * client (“…”, 「…」) and titles (《…》) are left out, and 迷你 is a word, not an address. The client brief
 * and the model-written follow-ups share this rule.
 */
export const informalAddress = (text: string): string | null =>
  /(?<!迷)[你妳][们們]?/.exec(text.replace(/“[^“”\n]{1,80}”|「[^「」\n]{1,80}」|《[^《》\n]{1,40}》/g, ''))?.[0] ?? null;

/** Plain-language names for the service enum, so the client brief never says "Aera Forge". */
export interface ServiceCatalogueEntry {
  id: ServiceId;
  name: L10n;
  /** What the client brief says instead of the name. */
  plain: L10n;
  /** Short neutral description the model sees. */
  description: L10n;
  capabilities: CapabilityTag[];
}
